import { Box, Container, Grid } from '@mui/material';
import { SuccessButton } from 'components/atoms/ButtonsOld';
import { BasicInput, PasswordInput } from 'components/atoms/Inputs';
import ValidationMessage from 'components/atoms/ValidationMessage';
import React, { FormEvent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUsersLoginInfo } from 'redux/actions/internal/general/users';
import { RootState } from 'redux/reducers';
import { useErrorFocus, useValidator } from 'services/validation/validator';
import { ValidationType } from 'types/validation';
import Collapse from '@mui/material/Collapse';
import { useSignInProcess } from 'hooks/useSignInProcess';
import useTranslation from 'next-translate/useTranslation';
import { LargeText, SmallText } from 'components/atoms/Typography';
import firebase from 'config/firebase';
import ModalPopup from 'components/molecules/ModalPopup';
import { PrimaryButton } from 'components/atoms/Buttons';
import { useSnackbar } from 'notistack';
import MailSent from 'assets/svg/mailSent.svg';
import { useExecutor } from 'hooks/useExecutor';
import { useLoginContext } from 'context/login-context';
import { useAuth } from 'context/authentication';

interface IEmailLoginProps {
    loading?: boolean;
    onContinue: (event: FormEvent, hasPassword: boolean, anotherProviders?: string[]) => void;
}

const EmailLogin = ({ loading, onContinue }: IEmailLoginProps) => {
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isForgotPasswordClicked, setIsForgotPasswordClicked] = useState<boolean>(false);
    const [resendLinkModalOpen, setResendLinkModalOpen] = useState<boolean>(false);
    const [emailInput, setEmailInput] = useState<string>('');
    const usersLoginInfo = useSelector((state: RootState) => state.usersLoginInfo.data);
    const validator = useValidator();
    const [checkForEmail, setCheckForEmail] = useState<boolean>(false);
    const passwordFocus = useErrorFocus('password', 'EMAIL_PASSWORD_LOGIN', 'LOGIN_BACKEND_ERROR');
    const passwordRef = useRef<HTMLInputElement>(null);
    const signInProcess = useSignInProcess();
    const { t } = useTranslation('common');
    const { enqueueSnackbar } = useSnackbar();
    const checkEmailExecutor = useExecutor('/customers/checkEmailProviders');
    const { update } = useLoginContext();

    const handleEmailChange = (email: string) => {
        if (showPassword) {
            setShowPassword(false);
            setCheckForEmail(false);
        }
        if (checkForEmail) validator.validate('EMAIL_LOGIN', { email: usersLoginInfo.email });
        dispatch(updateUsersLoginInfo({ email }));
    };

    const handlePasswordChange = (password: string) => {
        validator.resetValidation('LOGIN_BACKEND_ERROR', 'EMAIL_PASSWORD_LOGIN');
        dispatch(updateUsersLoginInfo({ password }));
    };

    const handleContinue = async (e: FormEvent) => {
        e.preventDefault();

        const type: ValidationType = showPassword ? 'EMAIL_PASSWORD_LOGIN' : 'EMAIL_LOGIN';
        let validationObject: { password?: string; email: string } = {
            email: usersLoginInfo.email,
        };
        if (showPassword) validationObject = { ...validationObject, password: usersLoginInfo.password };
        const valid = await validator.validate(type, validationObject);

        if (valid) {
            const response = await checkEmailExecutor.post({
                email: usersLoginInfo.email,
            });

            if (response.success) {
                update('userProviders', response.data);
                if (response.data.includes('password') && !showPassword) {
                    setShowPassword(true);
                    passwordRef.current?.focus();
                } else {
                    if (response.data.length > 0) {
                        // const response = await checkEmail(usersLoginInfo.email);
                        // if (response.success) {
                        //     const customer = response.data;
                        //     dispatch(
                        //         updateUsersLoginInfo({
                        //             name: customer?.firstName,
                        //             lastName: customer?.lastName,
                        //         })
                        //     );
                        // }
                        onContinue(e, showPassword, response.data);
                    } else {
                        onContinue?.(e, showPassword);
                    }
                }
            } else {
                if (response.data?.code === 'auth/user-not-found') {
                    onContinue?.(e, showPassword);
                }
            }
        } else {
            setCheckForEmail(true);
        }
    };

    const handleNoAccount = e => {
        e.preventDefault();
        signInProcess.nextStep('REGISTER_INFO');
    };

    const handleOpenForgotPasswordModal = () => {
        setIsForgotPasswordClicked(true);
    };

    const handleForgotPasswordClose = () => {
        setIsForgotPasswordClicked(false);
    };

    const handleResendLinkModalClose = () => {
        setResendLinkModalOpen(false);
    };

    const handleForgotPasswordSendLinkClick = () => {
        firebase
            .auth()
            .sendPasswordResetEmail(emailInput)
            .then(() => {
                setIsForgotPasswordClicked(false);
                setResendLinkModalOpen(true);
            })
            .catch(e => enqueueSnackbar(e.message, { variant: 'error' }));
    };

    const handleForgotPasswordEmailChange = (email: string) => {
        setEmailInput(email);
    };

    return (
        <Container>
            <Box mt={3}>
                <Grid direction="column" alignItems="center">
                    <form onSubmit={handleContinue} style={{ width: '100%' }} noValidate>
                        <Grid item style={{ width: '100%' }}>
                            <Box mt={1}>
                                <BasicInput
                                    placeholder={t('email')}
                                    onChange={handleEmailChange}
                                    error={validator.hasError('EMAIL_LOGIN', 'email')}
                                />
                                {validator.hasError('EMAIL_LOGIN', 'email') && <ValidationMessage type="EMAIL_LOGIN" />}
                            </Box>
                        </Grid>
                        <Grid item style={{ width: '100%', position: 'relative', paddingBottom: '15px' }}>
                            <Collapse in={showPassword}>
                                <Box mt={1}>
                                    <PasswordInput
                                        ref={passwordRef}
                                        onChange={handlePasswordChange}
                                        error={
                                            validator.hasError('LOGIN_BACKEND_ERROR', 'password') ||
                                            validator.hasError('EMAIL_PASSWORD_LOGIN', 'password')
                                        }
                                        focused={passwordFocus}
                                    />
                                    {validator.hasError('EMAIL_PASSWORD_LOGIN', 'password') && (
                                        <ValidationMessage type="EMAIL_PASSWORD_LOGIN" />
                                    )}
                                    {validator.hasError('LOGIN_BACKEND_ERROR', 'password') && (
                                        <ValidationMessage type="LOGIN_BACKEND_ERROR" />
                                    )}
                                </Box>
                                <SmallText
                                    sx={{
                                        position: 'absolute',
                                        right: 0,
                                        '&:hover': {
                                            cursor: 'pointer',
                                        },
                                    }}
                                    onClick={handleOpenForgotPasswordModal}
                                >
                                    {t('forgotPassword')}
                                </SmallText>
                            </Collapse>
                        </Grid>
                        <Grid item style={{ width: '100%' }}>
                            <Box my={2}>
                                <SuccessButton loading={loading}>{t('continue')}</SuccessButton>
                            </Box>
                        </Grid>
                        {/* <Grid item style={{ width: '100%' }}>
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                mt: { xs: 3, lg: 0 },
                            }}
                        >
                            <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'right' }}>
                                <Link
                                    onClick={handleNoAccount}
                                    variant="body2"
                                    sx={{
                                        '&:hover': {
                                            cursor: 'pointer',
                                        },
                                    }}
                                >
                                    {t('doNotHaveAccount')}
                                </Link>
                            </Grid>
                            <Grid item xs={12} sm={6} className={classes.tertiaryAction}>
                                <Link href="javascript:void(0)" variant="body2" color="initial">
                                    Forgot password?
                                </Link>
                            </Grid>
                        </Grid>
                    </Grid> */}
                        <Box mt={1}>
                            <Grid container justifyContent="flex-start">
                                {/* <ValidationMessage type="EMAIL_LOGIN" /> */}
                                {/* <ValidationMessage type="EMAIL_PASSWORD_LOGIN" /> */}
                                {!validator.hasError('LOGIN_BACKEND_ERROR', 'password') && (
                                    <ValidationMessage type="LOGIN_BACKEND_ERROR" />
                                )}
                            </Grid>
                        </Box>
                    </form>
                </Grid>
                <ModalPopup open={isForgotPasswordClicked} handleClose={handleForgotPasswordClose} minWidth="300px">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: { xs: '300px', sm: '500px' },
                            alignItems: 'center',
                            paddingTop: { xs: '20px', sm: '50px' },
                            paddingBottom: { xs: '20px', sm: '50px' },
                            paddingLeft: { xs: '20px', sm: '50px' },
                            paddingRight: { xs: '20px', sm: '50px' },
                        }}
                    >
                        <LargeText semibold>{t('resetPassword')}</LargeText>
                        <SmallText sx={{ paddingTop: '20px' }} semibold>
                            {t('resetPasswordDescription')}
                        </SmallText>
                        <Box mt={2} mb={2} width="100%">
                            <BasicInput
                                placeholder={t('email')}
                                onChange={handleForgotPasswordEmailChange}
                                error={validator.hasError('EMAIL_LOGIN', 'email')}
                            />
                            {validator.hasError('EMAIL_LOGIN', 'email') && <ValidationMessage type="EMAIL_LOGIN" />}
                        </Box>
                        <PrimaryButton fullWidth onClick={handleForgotPasswordSendLinkClick}>
                            {t('sendLink')}
                        </PrimaryButton>
                    </Box>
                </ModalPopup>
                <ModalPopup open={resendLinkModalOpen} handleClose={handleResendLinkModalClose} minWidth="300px">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '400px',
                            alignItems: 'center',
                            paddingTop: { xs: '20px', sm: '50px' },
                            paddingBottom: { xs: '20px', sm: '50px' },
                            paddingLeft: { xs: '20px', sm: '50px' },
                            paddingRight: { xs: '20px', sm: '50px' },
                        }}
                    >
                        <LargeText semibold>{t('checkMail')}</LargeText>
                        <img style={{ paddingTop: '40px', paddingBottom: '20px' }} src={MailSent} />
                        <SmallText sx={{ paddingTop: '20px' }} semibold>
                            {t('mailSent')}
                        </SmallText>
                        <Box paddingTop="50px">
                            <SmallText fontWeight="700">{t('didNotReceiveMail')}</SmallText>
                            <SmallText
                                onClick={handleForgotPasswordSendLinkClick}
                                sx={{ paddingLeft: '30px', textDecoration: 'underline', color: 'primary.main' }}
                            >
                                {t('resend')}.
                            </SmallText>
                        </Box>
                    </Box>
                </ModalPopup>
            </Box>
        </Container>
    );
};

export default EmailLogin;
